import { useEffect, useState } from 'react';
import type { User } from '../types';
import { subscriptionAPI } from '../services/api';
import { isAdminEmail } from './analytics';

type PlanEntitlements = { sms_enabled?: boolean; inbound_enabled?: boolean };

let _plansByIdCache: Record<string, PlanEntitlements> | null = null;
let _plansByIdFetchedAtMs: number | null = null;
let _plansFetchPromise: Promise<Record<string, PlanEntitlements>> | null = null;

const PLANS_CACHE_TTL_MS = 30_000;

async function fetchPlansById(opts?: { force?: boolean }): Promise<Record<string, PlanEntitlements>> {
  const force = Boolean(opts?.force);
  const fresh =
    _plansByIdCache &&
    _plansByIdFetchedAtMs != null &&
    (Date.now() - _plansByIdFetchedAtMs) < PLANS_CACHE_TTL_MS;

  if (!force && fresh) return _plansByIdCache!;
  if (_plansFetchPromise) return _plansFetchPromise;

  _plansFetchPromise = subscriptionAPI.getSubscriptionLinks().then((res: any) => {
    const plans = res?.plans || {};
    const mapped: Record<string, PlanEntitlements> = {};
    for (const [planId, plan] of Object.entries<any>(plans)) {
      mapped[String(planId).toLowerCase()] = {
        sms_enabled: Boolean((plan as any)?.sms_enabled),
        inbound_enabled: Boolean((plan as any)?.inbound_enabled),
      };
    }
    _plansByIdCache = mapped;
    _plansByIdFetchedAtMs = Date.now();
    return mapped;
  }).finally(() => {
    _plansFetchPromise = null;
  });

  return _plansFetchPromise;
}

export function invalidatePlansEntitlementsCache() {
  _plansByIdCache = null;
  _plansByIdFetchedAtMs = null;
  _plansFetchPromise = null;
}

function usePlanEligibility(user: User | null | undefined, key: keyof PlanEntitlements) {
  // Keep admin bypass aligned with AuthContext/AdminRoute logic (email whitelist),
  // while still honoring explicit backend admin flag when present.
  const isAdmin = Boolean(user?.is_admin) || isAdminEmail(user?.email);
  const planId = (user?.subscription_plan || '').toLowerCase();
  const [loading, setLoading] = useState<boolean>(Boolean(planId) && !isAdmin);
  const [eligible, setEligible] = useState<boolean>(isAdmin);

  useEffect(() => {
    let cancelled = false;
    const apply = (plansById: Record<string, PlanEntitlements>) => {
      if (cancelled) return;
      setEligible(Boolean(plansById[planId]?.[key]));
    };

    if (isAdmin) {
      setLoading(false);
      setEligible(true);
      return;
    }

    if (!planId) {
      setLoading(false);
      setEligible(false);
      return;
    }

    const refresh = (force: boolean = false, silent: boolean = false) => {
      if (!silent) setLoading(true);
      fetchPlansById({ force })
        .then(apply)
        .catch(() => { if (!cancelled) setEligible(false); })
        .finally(() => { if (!cancelled && !silent) setLoading(false); });
    };

    // Initial load (uses TTL cache if fresh) — show loading.
    refresh(false);

    // Background refresh hooks. These fire on every alt-tab / visibility flip,
    // so they MUST run silently — flipping `loading` here unmounts pages whose
    // route guard short-circuits on `entitlementsLoading` (e.g. InboundCallsGuard
    // in App.tsx), causing a full page remount + refetch on every focus event.
    const onFocus = () => refresh(false, true);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(false, true); };
    // Explicit invalidation (e.g. plan changed in another tab) shows loading.
    const onEntitlementsUpdated = () => refresh(true);

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('entitlements:updated', onEntitlementsUpdated as EventListener);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('entitlements:updated', onEntitlementsUpdated as EventListener);
    };
  }, [isAdmin, planId, key]);

  return { eligible, loading };
}

export function useSmsEligibility(user: User | null | undefined) {
  const { eligible, loading } = usePlanEligibility(user, 'sms_enabled');
  // LT-238: SMS is now supported in India (MSG91/DLT). Eligibility falls back
  // to plan-level entitlement, same as AU.
  return { canSms: eligible, loading };
}

export function useInboundEligibility(user: User | null | undefined) {
  const { eligible, loading } = usePlanEligibility(user, 'inbound_enabled');
  return { canInbound: eligible, loading };
}

